import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { twMerge } from "tailwind-merge";
import {
    ArrowRight,
    ChevronRight,
    Crown,
    KeyRound,
    ListOrdered,
    Lock,
    TriangleAlert,
    User,
    WifiOff,
    X,
} from "lucide-react";
import {
    judgeGetEvent,
    judgeSaveCard,
    judgeSignIn,
} from "../utils/firebase";
import {
    FRESH_ROUND,
    ROUNDS,
    emptyRounds,
    flipCorners,
    isStoppage,
    lockedRoundsOf,
    methodsFor,
    missingRounds,
    scoredCount,
    stepScore,
    tallyRounds,
} from "../utils/judge-scoring";
import {
    classifySave,
    enqueue,
    isLegacyScored,
    mergeCard,
    nextWakeIn,
    pickNext,
    restoreQueue,
    retryDelay,
    serializeQueue,
    unlockedRounds,
    withPendingLocks,
} from "../utils/judge-queue";
import { followOrder } from "../utils/running-order";
import { fighterEntryDisplayMapping, weightClassesName } from "../constants";

/* The judge's scoring page, /judge/:token.
 *
 * No Firebase Auth: the link token plus a 4-digit PIN open a session through
 * the judgeSignIn Cloud Function, and every later call carries that session.
 * The server re-checks it each time and answers SESSION_ENDED when the admin
 * changed the PIN, revoked the judge or replaced the link; we then drop back
 * to the PIN screen.
 *
 * Scoring follows the 10-point must system (utils/judge-scoring.js). The
 * round on screen starts at 10–10; NEXT ROUND records it as shown and LOCKS
 * it on the server (commitRound): from then on the round is read-only and
 * published to the Partner API. Drafts also go to the server on a round-tab
 * change and when switching match, so a judge can pick up on another device;
 * those don't lock. Only an admin can unlock a round (unlockJudgeRound).
 * A submitted card is locked; the server refuses changes to it.
 *
 * Saves run one at a time from a queue (utils/judge-queue.js): each card's
 * in order, but a card waiting out a retry never holds up another's. A save
 * that fails for a network reason is retried with backoff while an
 * "offline, retrying" badge shows; one the server refuses is dropped and
 * what the page assumed (a round locked by NEXT ROUND) is put back as the
 * server has it. The queue is kept in localStorage, so a reload sends what
 * was left. The board re-reads the running order and the cards every little
 * while, so an admin unlock or a pulled-out match reaches the judge without
 * a reload.
 *
 * Judges don't reorder matches: the admin does, for all three at once, on
 * the live results page (a fighter in the toilet or running late). A judge
 * waiting on their next match with nothing scored on it is put on the new
 * next match; one already scoring stays put (utils/running-order.js
 * followOrder). When the match on screen changed, or its number did, a
 * yellow banner says so until the judge taps OK (and the phone buzzes where
 * it can); other moves are a toast. Besides the timer, the order is re-read
 * straight after a submit (so NEXT MATCH is the right one), when the page
 * comes back on screen and when the match list opens. */

// How often the board re-reads the running order and its cards (a reorder,
// admin unlocks, other devices). The results page tells the admin a reorder
// reaches the judges within this.
const REFRESH_MS = 10000;
// NEXT ROUND stays disabled until its commit lands (at least MIN, so a
// double tap is one tap), but never longer than MAX: offline, the commit
// waits in the queue and the judge scores on.
const NEXT_HOLD_MIN_MS = 450;
const NEXT_HOLD_MAX_MS = 1500;

const SESSION_KEY = (token) => `mfl-judge-session:${token}`;
const LOCK_KEY = (token) => `mfl-judge-lock:${token}`;
// Unsent saves, per judge: sent again after a reload (or a new sign-in).
const QUEUE_KEY = (token, judgeId) => `mfl-judge-queue:${token}:${judgeId}`;
const DEVICE_KEY = "mfl-judge-device";

/**
 * Other judges with saves still waiting on this device for this link. They
 * can only go with their own judge's session, so they wait until that judge
 * signs in here again.
 */
const otherJudgesQueued = (token, judgeId) => {
    const prefix = QUEUE_KEY(token, "");
    const out = [];
    try {
        for (let i = 0; i < window.localStorage.length; i += 1) {
            const key = window.localStorage.key(i);
            if (!key || !key.startsWith(prefix) || key === QUEUE_KEY(token, judgeId)) continue;
            if (restoreQueue(store.get(key), () => 0).length) out.push(key.slice(prefix.length));
        }
    } catch {
        /* storage unavailable: nothing can be waiting */
    }
    return out;
};

// Toasts sit at the top here: the call-to-action owns the bottom edge.
const TOAST = { position: "top-center" };

// A short double buzz, where the device can (Android; not iPhone or iPad).
const buzz = () => {
    try {
        window.navigator.vibrate?.([150, 100, 150]);
    } catch {
        /* not supported: the banner is enough */
    }
};

const store = {
    get(key) {
        try {
            return JSON.parse(window.localStorage.getItem(key));
        } catch {
            return null;
        }
    },
    set(key, value) {
        try {
            window.localStorage.setItem(key, JSON.stringify(value));
        } catch {
            /* private mode: the session just won't survive a reload */
        }
    },
    remove(key) {
        try {
            window.localStorage.removeItem(key);
        } catch {
            /* ignore */
        }
    },
};

/** A random id for this browser, so wrong PINs can be counted per device. */
const deviceId = () => {
    let id = store.get(DEVICE_KEY);
    if (typeof id !== "string" || id.length < 16) {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        id = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
        store.set(DEVICE_KEY, id);
    }
    return id;
};

const blankCard = (boutKey) => ({
    boutKey,
    rounds: emptyRounds(),
    submitted: false,
    endAt: null,
    method: null,
    winner: null,
    lockedRounds: Array.from({ length: ROUNDS }, () => false),
});

const normaliseCard = (card) => ({
    ...blankCard(card.boutKey),
    ...card,
    rounds: Array.from({ length: ROUNDS }, (_, i) => card.rounds?.[i] ?? null),
    lockedRounds: lockedRoundsOf(card),
});

const sameCard = (a, b) =>
    JSON.stringify([a.rounds, a.submitted, a.endAt, a.method, a.winner, a.lockedRounds]) ===
    JSON.stringify([b.rounds, b.submitted, b.endAt, b.method, b.winner, b.lockedRounds]);

// How a card's method reads: DECISION and SUBMISSION are words, KO and TKO
// stay as they are. The short form (the match list) says SUB.
const methodName = (m, t) =>
    m === "DEC" ? t("judge.end.decision") : m === "SUB" ? t("judge.end.submission") : m;
const methodShort = (m, t) => (m === "SUB" ? t("judge.end.submissionShort") : m);

const shortName = (name) => {
    const [first, second] = String(name || "").split(/\s+/);
    return second ? `${first} ${second[0]}.` : first || "";
};

// The first round still open for scoring: not locked (an admin-unlocked
// round comes first) and, failing that, the last one.
// Rounds lock in order (the server refuses anything else): the next round
// to lock is the first one that isn't locked. An admin-unlocked round is
// just that. null when every round is locked.
const nextOpenRound = (card) => {
    const i = (card.lockedRounds ?? []).indexOf(false);
    return i < 0 ? null : i + 1;
};

// Where a card opens: its next round to lock, else the last round.
const firstOpenRound = (card) => nextOpenRound(card) ?? ROUNDS;

// The round a reopened card was submitted at (its stoppage round, or 5).
const reopenedLast = (card) => card.reopenedFrom?.endAt ?? ROUNDS;

// A reopened card whose corrected rounds are all locked again: ready for
// the judge to submit again with their PIN.
const readyToResubmit = (card) =>
    !!card.reopenedFrom &&
    !card.submitted &&
    card.lockedRounds.slice(0, reopenedLast(card)).every(Boolean);

/* ─────────────────────────────── layout ─────────────────────────────── */

const LOGO_SRC = "/assets/img/logo-mfl.png";

// Signed in, phones get the logo inside the header row instead of a bar of
// its own: every row of height goes to the scorecard.
function JudgeShell({ children, barOnPhone = true }) {
    return (
        <div
            className={twMerge(
                "h-[100dvh] flex flex-col bg-base-100 text-base-content overflow-hidden",
                !barOnPhone && "pt-[env(safe-area-inset-top)] md:pt-0"
            )}
        >
            <header
                className={twMerge(
                    "flex-none flex justify-center items-center border-b-2 border-gray-100 bg-gray-400/10 py-1 pt-[max(0.25rem,env(safe-area-inset-top))]",
                    !barOnPhone && "hidden md:flex"
                )}
            >
                <img className="h-8 object-contain" src={LOGO_SRC} alt="Malaysia Fight League" />
            </header>
            {children}
        </div>
    );
}

/* ────────────────────────────── PIN screen ──────────────────────────── */

function PinScreen({ token, onSignedIn }) {
    const { t } = useTranslation();
    const [pin, setPin] = useState("");
    const [busy, setBusy] = useState(false);
    const [lockedUntil, setLockedUntil] = useState(
        () => Number(store.get(LOCK_KEY(token))) || 0
    );
    const [now, setNow] = useState(Date.now());
    const inputRef = useRef(null);

    const locked = lockedUntil > now;
    const minutesLeft = Math.max(1, Math.ceil((lockedUntil - now) / 60000));

    useEffect(() => {
        if (!locked) return undefined;
        const id = setInterval(() => setNow(Date.now()), 15000);
        return () => clearInterval(id);
    }, [locked]);

    const lock = (until) => {
        setLockedUntil(until);
        setNow(Date.now());
        store.set(LOCK_KEY(token), until);
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (pin.length !== 4 || busy || locked) return;
        setBusy(true);
        try {
            const res = await judgeSignIn({ token, pin, deviceId: deviceId() });
            if (res?.success) {
                store.remove(LOCK_KEY(token));
                // An earlier "Invalid PIN" toast would otherwise sit over
                // the first scoring screen for the rest of its 4 seconds.
                toast.dismiss();
                onSignedIn({ sessionId: res.sessionId, secret: res.secret });
                return;
            }
            setPin("");
            if (res?.code === "LOCKED") {
                lock(res.lockedUntil || Date.now() + 15 * 60000);
                toast.error(
                    t("judge.pin.tooMany", {
                        count: Math.max(
                            1,
                            Math.ceil(((res.lockedUntil || 0) - Date.now()) / 60000)
                        ),
                    }),
                    TOAST
                );
            } else if (res?.code === "INVALID") {
                toast.error(t("judge.pin.invalid", { count: res.triesLeft ?? 0 }), TOAST);
                inputRef.current?.focus();
            } else {
                toast.error(t("judge.pin.failed"), TOAST);
            }
        } catch (error) {
            console.error("judgeSignIn failed:", error);
            toast.error(t("judge.pin.failed"), TOAST);
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className="flex-1 overflow-y-auto flex">
            <form
                onSubmit={submit}
                className="m-auto w-full max-w-sm px-4 py-8 flex flex-col gap-4"
            >
                <div className="text-center">
                    <KeyRound size={40} strokeWidth={1.5} className="mx-auto text-white" />
                    <p className="mt-2 font-bold uppercase tracking-wider text-white text-lg">
                        {t("judge.pin.title")}
                    </p>
                </div>
                {/* Same box as the live-stream access code on the event page. */}
                <label htmlFor="judge-pin" className="font-semibold">
                    {t("judge.pin.label")}
                </label>
                <input
                    ref={inputRef}
                    id="judge-pin"
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    autoFocus
                    className="input input-bordered text-center text-lg tracking-widest"
                    placeholder="••••"
                    value={pin}
                    disabled={locked || busy}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                />
                <button
                    type="submit"
                    className="btn btn-primary w-full"
                    disabled={pin.length !== 4 || locked || busy}
                >
                    {busy ? (
                        <span className="loading loading-spinner loading-xs" />
                    ) : locked ? (
                        t("judge.pin.locked", { minutes: minutesLeft })
                    ) : (
                        t("judge.pin.unlock")
                    )}
                </button>
            </form>
        </main>
    );
}

/* ─────────────────────────────── pieces ─────────────────────────────── */

function Portrait({ src }) {
    return (
        <div className="flex-none w-16 min-h-16 bg-base-200 overflow-hidden flex items-center justify-center">
            {src ? (
                <img src={src} alt="" className="w-full h-full object-cover object-center" />
            ) : (
                <User size={28} className="opacity-40" />
            )}
        </div>
    );
}

function FighterRows({ match }) {
    const meta = (c) => [c.record, c.team].filter(Boolean).join(" · ");
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {/* The card is the colour, so the portrait needs no frame. */}
            <div className="flex items-stretch bg-red-600 text-white min-w-0">
                <Portrait src={match.red.picture} />
                <div className="flex-1 min-w-0 px-3 py-2 flex flex-col justify-center gap-0.5">
                    <p className="font-bold truncate">{match.red.name}</p>
                    <p className="text-xs text-white/70 truncate">{meta(match.red)}</p>
                </div>
            </div>
            <div className="flex items-stretch bg-blue-600 text-white min-w-0">
                <div className="flex-1 min-w-0 px-3 py-2 flex flex-col justify-center items-end text-right gap-0.5">
                    <p className="font-bold truncate max-w-full">{match.blue.name}</p>
                    <p className="text-xs text-white/70 truncate max-w-full">{meta(match.blue)}</p>
                </div>
                <Portrait src={match.blue.picture} />
            </div>
        </div>
    );
}

/**
 * "MMA · D: Bantamweight". When the corners differ, both are listed in corner
 * order, red first: "MMA · C: Flyweight / D: Bantamweight".
 */
function matchCaption(match) {
    const entryOf = (c) => fighterEntryDisplayMapping[c.entry] || c.entry || null;
    const wcOf = (c) =>
        c.weightClass ? `${c.weightClass}: ${weightClassesName[c.weightClass] ?? ""}`.trim() : null;
    const both = (f) => {
        const [r, b] = [f(match.red), f(match.blue)];
        return r && b && r !== b ? `${r} / ${b}` : r || b;
    };
    return [both(entryOf), both(wcOf)].filter(Boolean).join(" · ");
}

function RoundTabs({ card, round, cutFrom, onPick }) {
    return (
        <div className="flex gap-1">
            {Array.from({ length: ROUNDS }, (_, i) => i + 1).map((n) => {
                const s = card.rounds[n - 1];
                return (
                    <button
                        key={n}
                        type="button"
                        onClick={() => onPick(n)}
                        className={twMerge(
                            "btn flex-1 h-12 min-h-12 px-0 flex-col gap-0.5",
                            n === round && "btn-primary",
                            cutFrom && n > cutFrom && "opacity-25 line-through"
                        )}
                    >
                        <b className="text-base leading-none flex items-center gap-0.5">
                            {n}
                            {card.lockedRounds?.[n - 1] && !card.submitted && (
                                <Lock size={10} aria-label="locked" />
                            )}
                        </b>
                        <small className="text-[0.625rem] font-medium opacity-70 leading-none">
                            {s ? `${s.red}–${s.blue}` : "—"}
                        </small>
                    </button>
                );
            })}
        </div>
    );
}

function ScoreFace({ side, score, other, steppers, onStep, t }) {
    const color = side === "red" ? "red" : "blue";
    const win = score === 10 && other < 10;
    const minus = steppers && onStep(-1, true);
    const plus = steppers && onStep(1, true);
    return (
        <div
            className={twMerge(
                "min-w-0 flex flex-col text-center border-2",
                color === "red" ? "border-red-600" : "border-blue-600",
                win && (color === "red" ? "bg-red-600" : "bg-blue-600")
            )}
        >
            {/* Square on phones; a fixed-height band on wider screens. */}
            <div className="aspect-square md:aspect-auto md:h-24 flex items-center justify-center">
                <span className="text-6xl font-bold text-white tabular-nums leading-none [text-shadow:0_2px_6px_rgba(0,0,0,0.7)]">
                    {score}
                </span>
            </div>
            {steppers && (
                <div
                    className={twMerge(
                        "grid grid-cols-2 border-t-2",
                        color === "red" ? "border-red-600" : "border-blue-600"
                    )}
                >
                    {[
                        [-1, "−", minus, "Minus"],
                        [1, "+", plus, "Plus"],
                    ].map(([delta, label, allowed, name], i) => (
                        <button
                            key={name}
                            type="button"
                            aria-label={t(`judge.score.${color}${name}`)}
                            disabled={!allowed}
                            onClick={() => onStep(delta)}
                            className={twMerge(
                                "btn border-0 h-12 min-h-12 px-0 text-2xl",
                                i === 1 && (color === "red" ? "border-l-2 border-red-600" : "border-l-2 border-blue-600"),
                                win &&
                                    "bg-black/20 text-white hover:bg-black/40 disabled:bg-black/20 disabled:text-white/40"
                            )}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function Totals({ card, t }) {
    const tot = tallyRounds(card.rounds);
    return (
        <div className="grid grid-cols-[1fr_auto_1fr] items-center mt-3 py-2 border-t-2 border-base-content/10">
            <span className="text-center text-2xl font-bold text-white tabular-nums">
                {tot.red}
                <small className="block text-[0.5625rem] font-bold tracking-[0.14em] opacity-60">
                    {t("judge.side.red")}
                </small>
            </span>
            <span className="text-xs opacity-60 tracking-wider">{t("judge.score.total")}</span>
            <span className="text-center text-2xl font-bold text-white tabular-nums">
                {tot.blue}
                <small className="block text-[0.5625rem] font-bold tracking-[0.14em] opacity-60">
                    {t("judge.side.blue")}
                </small>
            </span>
        </div>
    );
}

function MatchList({ matches, cards, current, onOpen, t }) {
    return (
        <div className="flex flex-col gap-1.5">
            {matches.map((m) => {
                const c = cards[m.boutKey] ?? blankCard(m.boutKey);
                const tot = tallyRounds(c.rounds);
                const scored = scoredCount(c.rounds);
                const isCur = m.boutKey === current;
                return (
                    <button
                        key={m.boutKey}
                        type="button"
                        onClick={() => onOpen(m.boutKey)}
                        className={twMerge(
                            "w-full flex items-center gap-2.5 border p-2.5 text-sm text-left hover:bg-base-200",
                            isCur && "bg-white text-black border-white hover:bg-white"
                        )}
                    >
                        <span
                            className={twMerge(
                                "flex-none w-6 h-6 border flex-center font-bold text-xs",
                                isCur && "border-black"
                            )}
                        >
                            {m.n}
                        </span>
                        <span className="flex-1 min-w-0 truncate">
                            {t("judge.list.versus", {
                                red: shortName(m.red.name),
                                blue: shortName(m.blue.name),
                            })}
                        </span>
                        <span
                            className={twMerge(
                                "flex-none font-mono text-xs flex items-center gap-1",
                                c.submitted && "opacity-70"
                            )}
                        >
                            {c.submitted ? (
                                <>
                                    {isStoppage(c.method)
                                        ? `${methodShort(c.method, t)} R${c.endAt ?? ROUNDS}`
                                        : `${tot.red}–${tot.blue}`}
                                    <Lock size={12} />
                                </>
                            ) : (
                                <>
                                    {scored ? `${scored}/${ROUNDS}` : "—"}
                                    <ChevronRight size={12} />
                                </>
                            )}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/* ──────────────────────────────── board ─────────────────────────────── */

function JudgeBoard({ data, session, token, onSessionEnded }) {
    const { t } = useTranslation();
    const { judge } = data;
    const [event, setEvent] = useState(data.event);
    // The running order. It is re-read with the cards, so a pulled-out,
    // removed or re-paired match comes and goes on a screen already open.
    const [matches, setMatches] = useState(data.matches);

    const itemIds = useRef(0);
    const queueKey = QUEUE_KEY(token, judge.id);
    // Saves not sent before the page was last closed, sent again in order.
    const restored = useRef(null);
    if (restored.current === null) {
        restored.current = restoreQueue(store.get(queueKey), () => (itemIds.current += 1));
    }
    // The server's last word on each card: what an admin unlock is measured
    // against, and what a refused save falls back to.
    const serverCards = useRef(null);
    if (serverCards.current === null) {
        serverCards.current = {};
        (data.cards ?? []).forEach((c) => {
            serverCards.current[c.boutKey] = normaliseCard(c);
        });
    }

    const [cards, setCards] = useState(() => {
        const map = {};
        data.matches.forEach((m) => {
            map[m.boutKey] = blankCard(m.boutKey);
        });
        Object.values(serverCards.current).forEach((c) => {
            map[c.boutKey] = c;
        });
        // Replayed saves show as they were left: the newest one's scores on
        // every round the server hasn't locked, NEXT ROUND locks pending
        // until they land.
        restored.current.forEach((q) => {
            const server = serverCards.current[q.boutKey] ?? blankCard(q.boutKey);
            // Scored before an admin swapped the corners: shown the new way
            // round (the save itself is turned round by the server).
            const m = data.matches.find((x) => x.boutKey === q.boutKey);
            const rounds = q.red && m && q.red === m.blue?.fighterId ? flipCorners({ rounds: q.rounds }).rounds : q.rounds;
            const merged = mergeCard({ ...server, rounds }, server, { keepLocal: true });
            map[q.boutKey] = {
                ...merged,
                lockedRounds: withPendingLocks(server.lockedRounds, restored.current, q.boutKey),
            };
        });
        return map;
    });
    const firstOpen = useCallback(
        (after = 0, from = cards) =>
            matches.find((m) => m.n > after && !from[m.boutKey]?.submitted)?.boutKey ??
            matches.find((m) => !from[m.boutKey]?.submitted)?.boutKey ??
            null,
        [matches, cards]
    );

    const [current, setCurrent] = useState(() => firstOpen() ?? matches[0]?.boutKey ?? null);
    const [round, setRound] = useState(() => {
        const c = cards[current];
        return c && !c.submitted ? firstOpenRound(c) : 1;
    });
    const [showList, setShowList] = useState(false);
    // The admin moved bouts and the match on screen changed (or its number
    // did): {boutKey, kind: "follow" | "renumbered"}, shown until OK.
    const [orderNote, setOrderNote] = useState(null);
    const [fresh, setFresh] = useState(false);
    const [ending, setEnding] = useState(false);
    const [method, setMethod] = useState("DEC");
    const [winner, setWinner] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [committing, setCommitting] = useState(false);
    const commitGate = useRef(0);
    // Submitting locks a card for good, so it asks for the PIN again.
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmPin, setConfirmPin] = useState("");
    const confirmPinRef = useRef(null);

    const dirty = useRef(new Set());
    // Pending saves, oldest first: { id, boutKey, rounds, commitRound, onDone }.
    // Mirrored to localStorage (per card) whenever it changes.
    const queue = useRef(restored.current);
    const inFlight = useRef(null);
    const pumping = useRef(false);
    const alive = useRef(true);
    // Per card: when its next try may go, tries at its oldest save, and
    // whether it is waiting out a network failure (the badge).
    const waitUntil = useRef({});
    const tries = useRef({});
    const netDown = useRef(new Set());
    const wake = useRef(null);
    // Per card: saves queued or finished. A refresh that began before one
    // of them leaves that card alone: its answer is older than the save.
    const seq = useRef({});
    const [retrying, setRetrying] = useState(false);
    const scrollRef = useRef(null);

    const match = matches.find((m) => m.boutKey === current);
    const card = cards[current] ?? blankCard(current);
    const readOnly = card.submitted || event.isEnded;
    const roundLocked = !!card.lockedRounds?.[round - 1];
    const nextOpen = nextOpenRound(card);
    // A later round the judge is only looking at: it can't be scored or
    // locked before the rounds ahead of it.
    const aheadOfOrder = !roundLocked && nextOpen !== null && round > nextOpen;
    const currentRef = useRef(current);
    currentRef.current = current;
    const cardsRef = useRef(cards);
    cardsRef.current = cards;
    const matchesRef = useRef(matches);
    matchesRef.current = matches;
    const endedRef = useRef(event.isEnded);
    endedRef.current = event.isEnded;
    // Mid-way through ending the match on screen: a reorder doesn't move it.
    const endingRef = useRef(ending);
    endingRef.current = ending || confirmOpen;

    useEffect(() => {
        scrollRef.current?.scrollTo?.({ top: 0 });
    }, [current, showList, fresh]);

    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
            wake.current?.();
        };
    }, []);

    // Another judge's unsent scores on this phone: say so at sign-in.
    useEffect(() => {
        if (!otherJudgesQueued(token, judge.id).length) return;
        toast(t("judge.otherJudgeQueued"), {
            ...TOAST,
            id: "other-judge-queued",
            duration: 12000,
            icon: <TriangleAlert size={20} className="text-warning flex-none" />,
        });
    }, [token, judge.id, t]);

    const setQueue = useCallback(
        (next) => {
            queue.current = next;
            if (next.length) store.set(queueKey, serializeQueue(next));
            else store.remove(queueKey);
        },
        [queueKey]
    );

    const bump = useCallback((boutKey) => {
        seq.current[boutKey] = (seq.current[boutKey] ?? 0) + 1;
    }, []);

    /** Drop queued saves (not one on its way); their NEXT ROUND holds end. */
    const dropQueued = useCallback(
        (keep) => {
            const gone = queue.current.filter((q) => !keep(q) && q.id !== inFlight.current?.id);
            if (!gone.length) return;
            setQueue(queue.current.filter((q) => !gone.includes(q)));
            gone.forEach((q) => {
                bump(q.boutKey);
                q.onDone?.();
            });
        },
        [bump, setQueue]
    );

    /** Lay the server's copy of a card under ours (see mergeCard). */
    const settleCard = useCallback((boutKey, server, { keepLocal }) => {
        setCards((prev) => {
            const merged = mergeCard(prev[boutKey] ?? blankCard(boutKey), server, { keepLocal });
            return {
                ...prev,
                [boutKey]: { ...merged, lockedRounds: withPendingLocks(merged.lockedRounds, queue.current, boutKey) },
            };
        });
    }, []);

    /**
     * An admin unlocked rounds of this card (the server had them locked and
     * now doesn't): say so, and on the card being shown go to the round.
     * NEXT ROUND on it then leads back to where the judge was.
     */
    const noticeUnlock = useCallback(
        (boutKey, before, after) => {
            const unlocked = unlockedRounds(before, after);
            if (!unlocked.length) return false;
            const n = matchesRef.current.find((m) => m.boutKey === boutKey)?.n;
            const reopened = !!before?.submitted && !after.submitted;
            toast(t(reopened ? "judge.unlockedReopened" : "judge.unlockedByAdmin", { rounds: unlocked.join(", "), n }), {
                ...TOAST,
                duration: 10000,
                id: `unlock:${boutKey}:${unlocked.join(",")}`,
            });
            if (boutKey === currentRef.current) {
                setFresh(false);
                setEnding(false);
                setRound(unlocked[0]);
            }
            return true;
        },
        [t]
    );

    /** A match that's no longer on the card leaves the list (and the screen). */
    const dropMatch = useCallback(
        (boutKey) => {
            const m = matchesRef.current.find((x) => x.boutKey === boutKey);
            if (!m) return;
            setMatches((prev) => prev.filter((x) => x.boutKey !== boutKey));
            toast.error(t("judge.matchGone", { n: m.n }), { ...TOAST, id: `gone:${boutKey}` });
        },
        [t]
    );

    /** The running order as the server has it now. */
    /** Our copy of one card turned round (its corners were swapped). */
    const flipLocal = useCallback((bk) => {
        setCards((prev) => (prev[bk] ? { ...prev, [bk]: flipCorners(prev[bk]) } : prev));
        if (bk === currentRef.current) setWinner((w) => (w === "red" ? "blue" : w === "blue" ? "red" : w));
    }, []);

    const applyMatches = useCallback(
        (list) => {
            const prev = matchesRef.current;
            const shape = (ms) => JSON.stringify(ms.map((m) => [m.boutKey, m.n, m.red, m.blue]));
            if (shape(prev) === shape(list)) return;
            const had = new Set(prev.map((m) => m.boutKey));
            const has = new Set(list.map((m) => m.boutKey));
            prev.filter((m) => !has.has(m.boutKey)).forEach((m) =>
                toast.error(t("judge.matchGone", { n: m.n }), { ...TOAST, id: `gone:${m.boutKey}` })
            );
            list.filter((m) => !had.has(m.boutKey)).forEach((m) =>
                toast(t("judge.matchAdded", { n: m.n }), { ...TOAST, id: `added:${m.boutKey}` })
            );
            setCards((prevCards) => {
                const add = {};
                list.forEach((m) => {
                    if (!prevCards[m.boutKey]) add[m.boutKey] = blankCard(m.boutKey);
                });
                return Object.keys(add).length ? { ...prevCards, ...add } : prevCards;
            });
            // An admin swapped a match's corners (only possible while no card
            // of it has reached the server): our copy turns round with it.
            list.forEach((m) => {
                const p = prev.find((x) => x.boutKey === m.boutKey);
                if (p && p.red?.fighterId && p.red.fighterId === m.blue?.fighterId && p.blue?.fighterId === m.red?.fighterId) {
                    flipLocal(m.boutKey);
                    toast(t("judge.cornersSwapped", { n: m.n }), { ...TOAST, id: `corners:${m.boutKey}`, duration: 10000 });
                }
            });
            // The admin moved bouts. Waiting on the next match with nothing
            // scored on it: on to the new next one. Scoring: stay put.
            const cur = currentRef.current;
            const busy =
                endingRef.current ||
                dirty.current.has(cur) ||
                queue.current.some((q) => q.boutKey === cur) ||
                inFlight.current?.boutKey === cur;
            const moved = followOrder(prev, list, cardsRef.current, cur, { busy });
            matchesRef.current = list;
            setMatches(list);
            if (!moved) return;
            const toNext = moved.follow && list.find((m) => m.boutKey === moved.follow);
            if (toNext) {
                const c = cardsRef.current[toNext.boutKey] ?? blankCard(toNext.boutKey);
                setCurrent(toNext.boutKey);
                setFresh(false);
                setEnding(false);
                setMethod("DEC");
                setWinner(null);
                setRound(firstOpenRound(c));
                setOrderNote({ boutKey: toNext.boutKey, kind: "follow" });
                buzz();
            } else if (moved.renumbered) {
                setOrderNote({ boutKey: cur, kind: "renumbered" });
                buzz();
            } else {
                toast(t("judge.orderChanged"), { ...TOAST, id: "order" });
            }
        },
        [flipLocal, t]
    );

    /**
     * The server has this match's corners the other way round from ours
     * (redFighterId is its red): an admin swapped them before our save
     * reached it, and it stored the save turned round. Turn the match round
     * here too, and our copy of the card unless the server's replaces it.
     */
    const turnRound = useCallback(
        (bk, serverRed, { flipCard = true } = {}) => {
            const m = matchesRef.current.find((x) => x.boutKey === bk);
            if (!m || !serverRed || m.red?.fighterId === serverRed || m.blue?.fighterId !== serverRed) return;
            const list = matchesRef.current.map((x) => (x.boutKey === bk ? { ...x, red: x.blue, blue: x.red } : x));
            matchesRef.current = list;
            setMatches(list);
            if (flipCard) flipLocal(bk);
            toast(t("judge.cornersSwapped", { n: m.n }), { ...TOAST, id: `corners:${bk}`, duration: 10000 });
        },
        [flipLocal, t]
    );

    // The order banner is about the match on screen: gone once it isn't.
    useEffect(() => {
        if (orderNote && orderNote.boutKey !== current) setOrderNote(null);
    }, [current, orderNote]);

    // The match on screen left the card: open the next one to score.
    useEffect(() => {
        if (current && matches.some((m) => m.boutKey === current)) return;
        const nextKey =
            matches.find((m) => !cardsRef.current[m.boutKey]?.submitted)?.boutKey ?? matches[0]?.boutKey ?? null;
        if (nextKey === current) return;
        const c = nextKey ? cardsRef.current[nextKey] ?? blankCard(nextKey) : null;
        setCurrent(nextKey);
        setShowList(false);
        setFresh(false);
        setEnding(false);
        setMethod("DEC");
        setWinner(null);
        setConfirmOpen(false);
        setRound(c && !c.submitted ? firstOpenRound(c) : 1);
    }, [current, matches]);

    const handleFailure = useCallback(
        (res, fallbackKey) => {
            if (res?.code === "SESSION_ENDED") {
                onSessionEnded();
                return;
            }
            if (res?.code === "EVENT_ENDED") {
                setEvent((e) => ({ ...e, isEnded: true }));
                toast.error(t("judge.errors.eventEnded"), TOAST);
                return;
            }
            if (res?.code === "CARD_LOCKED") {
                toast.error(t("judge.errors.cardLocked"), TOAST);
                return;
            }
            if (res?.code === "ROUND_LOCKED") {
                toast.error(t("judge.errors.roundLocked", { n: res.round ?? "" }), TOAST);
                return;
            }
            if (res?.code === "ROUND_ORDER") {
                toast.error(t("judge.errors.roundOrder", { n: res.round ?? "" }), TOAST);
                return;
            }
            if (res?.code === "NO_SUCH_MATCH") {
                toast.error(t("judge.errors.noSuchMatch"), TOAST);
                return;
            }
            if (res?.code === "SUB_NOT_MMA") {
                toast.error(t("judge.errors.subNotMma"), TOAST);
                return;
            }
            toast.error(t(fallbackKey), TOAST);
        },
        [onSessionEnded, t]
    );

    const refreshRef = useRef(null);
    const reads = useRef({ started: 0, applied: 0 });

    /** A save landed: the server's locks, plus NEXT ROUNDs still queued. */
    const onSaved = useCallback((item, res) => {
        if (!res.card) return;
        const bk = item.boutKey;
        const server = normaliseCard({ ...res.card, boutKey: bk });
        serverCards.current[bk] = server;
        setCards((prev) =>
            prev[bk]
                ? { ...prev, [bk]: { ...prev[bk], lockedRounds: withPendingLocks(server.lockedRounds, queue.current, bk) } }
                : prev
        );
    }, []);

    /**
     * The server refused a save. The card's later saves were built on it, so
     * they go too; the judge's scores stay on screen and go with their next
     * save. Whatever the page assumed (a round locked by NEXT ROUND) is put
     * back as the server has it.
     */
    const onRefused = useCallback(
        (item, res) => {
            const bk = item.boutKey;
            const code = res?.code;
            if (code === "EVENT_ENDED") {
                dropQueued(() => false);
                handleFailure(res, "judge.errors.saveFailed");
                return;
            }
            dropQueued((q) => q.boutKey !== bk);
            if ((code === "ROUND_ORDER" || code === "ROUND_LOCKED") && res.card) {
                const server = normaliseCard({ ...res.card, boutKey: bk });
                const before = serverCards.current[bk];
                serverCards.current[bk] = server;
                settleCard(bk, server, { keepLocal: true });
                dirty.current.add(bk);
                // Refused because an admin unlocked an earlier round: that's
                // the news, and where the judge goes.
                if (noticeUnlock(bk, before, server)) return;
                handleFailure(res, "judge.errors.saveFailed");
                if (code === "ROUND_ORDER" && bk === currentRef.current) setRound(firstOpenRound(server));
                return;
            }
            settleCard(bk, serverCards.current[bk] ?? blankCard(bk), { keepLocal: true });
            dirty.current.add(bk);
            if (code === "NO_SUCH_MATCH") {
                dropMatch(bk);
                return;
            }
            if (code === "CARD_LOCKED") {
                handleFailure(res, "judge.errors.saveFailed");
                setTimeout(() => refreshRef.current?.(), 300);
                return;
            }
            if (item.commitRound) {
                // A NEXT ROUND given up on (no answer the page could use):
                // the round isn't locked, so, as with ROUND_ORDER, the judge
                // goes back to it (or to an earlier one the server has open;
                // rounds lock in order) and presses NEXT ROUND again.
                const back = Math.min(item.commitRound, firstOpenRound(serverCards.current[bk] ?? blankCard(bk)));
                const n = matchesRef.current.find((m) => m.boutKey === bk)?.n;
                toast.error(t("judge.errors.commitFailed", { round: back, n }), { ...TOAST, duration: 8000 });
                if (bk === currentRef.current) setRound(back);
                return;
            }
            handleFailure(res, "judge.errors.saveFailed");
        },
        [dropMatch, dropQueued, handleFailure, noticeUnlock, settleCard, t]
    );

    /**
     * Sends queued saves, one at a time: each card's in order, and a card
     * waiting out a retry is stepped over so the others go on. A network
     * failure is retried with a growing pause while the badge shows; an
     * answer this page can't use is retried a few times, then given up.
     */
    const pump = useCallback(async () => {
        if (pumping.current) return;
        pumping.current = true;
        try {
            while (alive.current && queue.current.length) {
                const item = pickNext(queue.current, waitUntil.current, Date.now());
                if (!item) {
                    const ms = nextWakeIn(queue.current, waitUntil.current, Date.now()) ?? 0;
                    await new Promise((resolve) => {
                        wake.current = resolve;
                        setTimeout(resolve, Math.max(50, ms));
                    });
                    wake.current = null;
                    continue;
                }
                const bk = item.boutKey;
                let res = null;
                let networkError = false;
                const call = judgeSaveCard({
                    ...session,
                    boutKey: bk,
                    rounds: item.rounds,
                    ...(item.commitRound ? { commitRound: item.commitRound } : {}),
                    // The red corner these scores were given against.
                    ...(item.red ? { redFighterId: item.red } : {}),
                });
                inFlight.current = { id: item.id, boutKey: bk, promise: call.catch(() => null) };
                try {
                    res = await call;
                } catch (error) {
                    console.error("judgeSaveCard failed:", error);
                    networkError = true;
                }
                inFlight.current = null;
                if (!alive.current) break;
                if (res?.code === "SESSION_ENDED") {
                    // The queue stays stored: it's sent after the PIN.
                    onSessionEnded();
                    break;
                }
                if (res?.redFighterId) turnRound(bk, res.redFighterId);

                const n = (tries.current[bk] ?? 0) + 1;
                const verdict = classifySave({ res, networkError, tries: n });
                if (verdict === "retry") {
                    tries.current[bk] = n;
                    waitUntil.current[bk] = Date.now() + retryDelay(n) + Math.floor(Math.random() * 250);
                    if (networkError) netDown.current.add(bk);
                    setRetrying(netDown.current.size > 0);
                    continue;
                }
                delete tries.current[bk];
                delete waitUntil.current[bk];
                netDown.current.delete(bk);
                if (verdict === "ok" && netDown.current.size) {
                    // Through again: the cards waiting on the network go now.
                    netDown.current.forEach((b) => {
                        waitUntil.current[b] = 0;
                    });
                    netDown.current.clear();
                }
                setRetrying(netDown.current.size > 0);
                setQueue(queue.current.filter((q) => q.id !== item.id));
                bump(bk);
                item.onDone?.();
                if (verdict === "ok") onSaved(item, res);
                else onRefused(item, res);
            }
        } finally {
            pumping.current = false;
        }
        if (alive.current && !queue.current.length) setRetrying(false);
    }, [bump, onRefused, onSaved, onSessionEnded, session, setQueue, turnRound]);

    // Saves left from before a reload go out now.
    useEffect(() => {
        if (queue.current.length) pump();
    }, [pump]);

    /**
     * Queue a save of one card. `commitRound` (NEXT ROUND) locks that round
     * on the server; a plain draft locks nothing. Back-to-back drafts of the
     * same card collapse into the newest one.
     */
    const saveDraft = useCallback(
        (boutKey, rounds, commitRound = null, onDone = null) => {
            if (!boutKey || endedRef.current) return;
            dirty.current.delete(boutKey);
            itemIds.current += 1;
            const red = matchesRef.current.find((m) => m.boutKey === boutKey)?.red?.fighterId ?? null;
            const item = { id: itemIds.current, boutKey, rounds, commitRound, onDone, red };
            setQueue(enqueue(queue.current, item, inFlight.current?.id ?? null));
            bump(boutKey);
            wake.current?.();
            pump();
        },
        [bump, pump, setQueue]
    );

    /** Disable NEXT ROUND for one commit; returns the "it landed" callback. */
    const holdNext = () => {
        const id = (commitGate.current += 1);
        const started = Date.now();
        setCommitting(true);
        const release = () => {
            if (commitGate.current === id) setCommitting(false);
        };
        const cap = setTimeout(release, NEXT_HOLD_MAX_MS);
        return () =>
            setTimeout(() => {
                clearTimeout(cap);
                release();
            }, Math.max(0, NEXT_HOLD_MIN_MS - (Date.now() - started)));
    };

    /**
     * Re-read the running order and the cards: an admin unlock, a reopened
     * card, a match pulled or put back reach the judge without a reload.
     * A card with a save queued, on its way or answered since the read began
     * is left alone (its save is newer than what was read). One with unsent
     * scores keeps them on its open rounds.
     */
    const refresh = useCallback(async () => {
        if (document.hidden) return;
        const seqAtStart = { ...seq.current };
        const busyAtStart = new Set(queue.current.map((q) => q.boutKey));
        // Reads can overlap (the timer, a submit, the page coming back): an
        // older answer landing after a newer one would put back the old
        // running order, so it's dropped.
        const mine = (reads.current.started += 1);
        let res;
        try {
            res = await judgeGetEvent(session);
        } catch {
            return; // offline: the next tick tries again
        }
        if (!alive.current || mine < reads.current.applied) return;
        reads.current.applied = mine;
        if (res?.code === "SESSION_ENDED") {
            onSessionEnded();
            return;
        }
        if (!res?.success) return;
        if (res.event?.isEnded) setEvent((e) => ({ ...e, isEnded: true }));
        if (Array.isArray(res.matches)) applyMatches(res.matches);
        const updates = {};
        (res.cards ?? []).forEach((c) => {
            const bk = c.boutKey;
            if (
                busyAtStart.has(bk) ||
                queue.current.some((q) => q.boutKey === bk) ||
                (seq.current[bk] ?? 0) !== (seqAtStart[bk] ?? 0)
            ) {
                return;
            }
            const theirs = normaliseCard(c);
            const before = serverCards.current[bk];
            serverCards.current[bk] = theirs;
            const mine = cardsRef.current[bk] ?? blankCard(bk);
            const merged = mergeCard(mine, theirs, { keepLocal: dirty.current.has(bk) });
            if (!sameCard(mine, merged) || (mine.reopenedFrom ?? null) !== (merged.reopenedFrom ?? null)) {
                updates[bk] = merged;
            }
            noticeUnlock(bk, before, theirs);
        });
        if (Object.keys(updates).length) setCards((prev) => ({ ...prev, ...updates }));
    }, [applyMatches, noticeUnlock, onSessionEnded, session]);
    refreshRef.current = refresh;

    useEffect(() => {
        if (event.isEnded) return undefined;
        const id = setInterval(() => refreshRef.current?.(), REFRESH_MS);
        return () => clearInterval(id);
    }, [event.isEnded]);

    const setRounds = (boutKey, rounds) => {
        dirty.current.add(boutKey);
        setCards((prev) => ({ ...prev, [boutKey]: { ...prev[boutKey], rounds } }));
    };

    /** The round on screen, recorded as shown (an untouched 10–10 included). */
    const committed = () => {
        const rounds = card.rounds.slice();
        rounds[round - 1] = rounds[round - 1] ?? { ...FRESH_ROUND };
        return rounds;
    };

    const flushCurrent = () => {
        if (current && dirty.current.has(current) && !readOnly && match) {
            saveDraft(current, cards[current].rounds);
        }
    };

    // Leaving or reloading the page: unsent scores go into the (stored)
    // queue, so they're sent when the page is back.
    const flushRef = useRef(null);
    flushRef.current = flushCurrent;
    useEffect(() => {
        const onHide = () => flushRef.current?.();
        // Back on screen (a phone woken up): re-read now, not at the next tick.
        const onVisibility = () => {
            if (document.hidden) onHide();
            else refreshRef.current?.();
        };
        window.addEventListener("pagehide", onHide);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("pagehide", onHide);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    const openMatch = (boutKey) => {
        flushCurrent();
        const c = cards[boutKey] ?? blankCard(boutKey);
        setCurrent(boutKey);
        setShowList(false);
        setFresh(false);
        setEnding(false);
        setMethod("DEC");
        setWinner(null);
        setRound(c.submitted ? 1 : firstOpenRound(c));
    };

    const pickRound = (n) => {
        flushCurrent();
        setRound(n);
    };

    const step = (side) => (delta, dryRun) => {
        if (roundLocked || aheadOfOrder) return false;
        const next = stepScore(card.rounds[round - 1] ?? FRESH_ROUND, side, delta);
        if (dryRun) return !!next;
        if (!next) return false;
        const rounds = card.rounds.slice();
        rounds[round - 1] = next;
        setRounds(current, rounds);
        return true;
    };

    // NEXT ROUND: record the round as shown and lock it (on the server once
    // the save lands; here at once, so it can't be touched meanwhile, and
    // undone if the server refuses). On a round that is already locked it
    // just moves on. Only the next round in order locks; a later round is
    // look-only.
    const advance = () => {
        if (committing) return;
        if (roundLocked) {
            if (round < ROUNDS) setRound(round + 1);
            return;
        }
        if (round !== nextOpen) return;
        const rounds = committed();
        const lockedRounds = card.lockedRounds.slice();
        lockedRounds[round - 1] = true;
        dirty.current.delete(current);
        const next = { ...card, rounds, lockedRounds };
        setCards((prev) => ({ ...prev, [current]: { ...prev[current], rounds, lockedRounds } }));
        saveDraft(current, rounds, round, holdNext());
        // A reopened card with its correction locked again: straight to
        // submitting it again, as it was ended before.
        if (readyToResubmit(next)) {
            startResubmit(next);
            return;
        }
        // On to the next round to lock: normally the one after; after an
        // admin unlocked an earlier round, the one the judge was scoring.
        setRound(nextOpenRound(next) ?? ROUNDS);
    };

    /** Put a reopened card back on its submit step and ask for the PIN. */
    const startResubmit = (c) => {
        const from = c.reopenedFrom || {};
        const last = reopenedLast(c);
        setRound(last);
        if (from.endAt || isStoppage(from.method)) {
            setEnding(true);
            setMethod(from.method || "DEC");
            setWinner(isStoppage(from.method) ? from.winner ?? null : null);
        } else {
            setEnding(false);
            setMethod("DEC");
            setWinner(null);
        }
        setConfirmPin("");
        setConfirmOpen(true);
    };

    const askSubmit = () => {
        setConfirmPin("");
        setConfirmOpen(true);
    };

    useEffect(() => {
        if (confirmOpen) confirmPinRef.current?.focus();
    }, [confirmOpen]);

    const submit = async () => {
        if (confirmPin.length !== 4) return;
        const rounds = committed();
        const payload = ending
            ? {
                  rounds: rounds.map((r, i) => (i < round ? r : null)),
                  endAt: round,
                  method,
                  winner: isStoppage(method) ? winner : null,
              }
            : { rounds, endAt: null, method: "DEC", winner: null };
        const bk = current;
        setSubmitting(true);
        try {
            // The submit carries every round, so queued saves of this card
            // are dropped; one already on its way is let land first so it
            // can't race the submit.
            dropQueued((q) => q.boutKey !== bk);
            if (inFlight.current?.boutKey === bk) await inFlight.current.promise;
            bump(bk);
            const res = await judgeSaveCard({
                ...session,
                boutKey: bk,
                submit: true,
                pin: confirmPin,
                redFighterId: matchesRef.current.find((m) => m.boutKey === bk)?.red?.fighterId,
                ...payload,
            });
            bump(bk);
            // Stored the other way round (the corners were swapped): the
            // match turns round; the card is the server's from here.
            if (res?.redFighterId) turnRound(bk, res.redFighterId, { flipCard: !res.success });
            if (res?.success) {
                // Anything of this card still waiting (a retry) is moot now.
                dropQueued((q) => q.boutKey !== bk);
                dirty.current.delete(bk);
                serverCards.current[bk] = normaliseCard(res.card);
                setCards((prev) => ({ ...prev, [bk]: normaliseCard(res.card) }));
                setConfirmOpen(false);
                setConfirmPin("");
                setEnding(false);
                setFresh(true);
                // NEXT MATCH goes by the running order: have the latest.
                refreshRef.current?.();
            } else if (res?.code === "BAD_PIN") {
                setConfirmPin("");
                confirmPinRef.current?.focus();
                toast.error(t("judge.confirm.badPin", { count: res.triesLeft ?? 0 }), TOAST);
            } else {
                setConfirmOpen(false);
                if ((res?.code === "ROUND_LOCKED" || res?.code === "ROUND_ORDER") && res.card) {
                    const server = normaliseCard({ ...res.card, boutKey: bk });
                    const before = serverCards.current[bk];
                    serverCards.current[bk] = server;
                    settleCard(bk, server, { keepLocal: true });
                    dirty.current.add(bk);
                    if (noticeUnlock(bk, before, server)) return;
                }
                if (res?.code === "NO_SUCH_MATCH") {
                    dropMatch(bk);
                    return;
                }
                handleFailure(res, "judge.errors.submitFailed");
            }
        } catch (error) {
            console.error("judgeSaveCard submit failed:", error);
            toast.error(t("judge.errors.submitFailed"), TOAST);
        } finally {
            setSubmitting(false);
        }
    };

    if (!match) {
        return (
            <main className="flex-1 flex-center p-4 text-center">
                <p className="opacity-70">{t("judge.noMatches")}</p>
            </main>
        );
    }

    const sideLabel = (s) => (s === "red" ? t("judge.side.red") : t("judge.side.blue"));
    // How a match can end: DECISION · KO · TKO, and SUBMISSION in MMA. Four
    // buttons sit 2×2 on phones and in one row from sm up; three, one row.
    const methods = methodsFor(!!match.isMma);
    const methodRow = methods.length > 3 ? "grid grid-cols-2 sm:grid-cols-4 gap-1" : "flex gap-1";
    const outcome = (c) =>
        isStoppage(c.method)
            ? t("judge.outcome.stoppage", { method: methodName(c.method, t), side: sideLabel(c.winner), n: c.endAt ?? ROUNDS })
            : c.endAt
            ? t("judge.outcome.early", { n: c.endAt })
            : t("judge.outcome.decision");

    const header = (
        <div className="grid grid-cols-[1fr_auto_1fr] md:flex md:justify-between items-center gap-2">
            <button
                type="button"
                // No wrapping (.btn wraps): a long label ("Perlawanan 12") widens its column instead.
                className="btn btn-sm md:hidden justify-self-start flex-nowrap whitespace-nowrap"
                onClick={() => {
                    flushCurrent();
                    setShowList(true);
                    refreshRef.current?.();
                }}
                title={t("judge.list.open")}
            >
                <ListOrdered size={16} aria-hidden="true" />
                {t("judge.matchN", { n: match.n })}
            </button>
            <b className="hidden md:block text-xs tracking-widest uppercase opacity-60">
                {t("judge.matchN", { n: match.n })}
            </b>
            <img
                className="md:hidden h-7 object-contain justify-self-center"
                src={LOGO_SRC}
                alt="Malaysia Fight League"
            />
            {retrying ? (
                <span className="badge badge-warning gap-1 justify-self-end max-w-full truncate" role="status">
                    <WifiOff size={12} className="flex-none" />
                    {t("judge.offlineRetrying")}
                </span>
            ) : readOnly && !fresh ? (
                <span className="badge badge-ghost gap-1 justify-self-end">
                    <Lock size={12} />
                    {event.isEnded && !card.submitted ? t("judge.eventEndedBadge") : t("judge.locked")}
                </span>
            ) : (
                <span className="badge justify-self-end max-w-full truncate">{judge.name}</span>
            )}
        </div>
    );

    /* ── phone: the match list is its own screen ── */
    const listScreen = (
        <>
            <div className="flex-none px-3 pt-3 pb-2 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                <b className="text-xs tracking-widest uppercase opacity-60 truncate">
                    {event.isTest && <span className="text-warning">{t("judge.testBadge")} · </span>}
                    {t("judge.list.title", { name: event.name, count: matches.length })}
                </b>
                <img className="h-7 object-contain" src={LOGO_SRC} alt="Malaysia Fight League" />
                <span className="badge justify-self-end max-w-full truncate">{judge.name}</span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
                <MatchList matches={matches} cards={cards} current={current} onOpen={openMatch} t={t} />
            </div>
            <Cta>
                <button type="button" className="btn btn-primary w-full" onClick={() => setShowList(false)}>
                    {t("judge.backTo", { n: match.n })}
                    <ArrowRight size={18} />
                </button>
            </Cta>
        </>
    );

    const next = firstOpen(match.n);
    const caption = matchCaption(match);

    let body;
    let cta;
    if (card.submitted && fresh) {
        const tot = tallyRounds(card.rounds);
        body = (
            <>
                <FighterRows match={match} />
                <div className="flex flex-col items-center justify-center text-center gap-2 py-10">
                    <Lock size={44} className="text-white" />
                    <span className="badge badge-success">{t("judge.submitted")}</span>
                    <span className="text-4xl font-bold text-white tabular-nums leading-none">
                        {isStoppage(card.method)
                            ? `${methodName(card.method, t)} · ${sideLabel(card.winner)}`
                            : `${tot.red} — ${tot.blue}`}
                    </span>
                    <span className="text-xs opacity-60 tracking-wider">
                        {isStoppage(card.method)
                            ? `${t("judge.roundN", { n: card.endAt ?? ROUNDS }).toUpperCase()} · `
                            : card.endAt
                            ? `${t("judge.outcome.endedAfter", { n: card.endAt })} · `
                            : ""}
                        {t("judge.blind")}
                    </span>
                </div>
            </>
        );
        cta = next ? (
            <button type="button" className="btn btn-primary w-full" onClick={() => openMatch(next)}>
                {t("judge.nextMatch")}
                <ArrowRight size={18} />
            </button>
        ) : (
            <AllMatchesButton onClick={() => setShowList(true)} t={t} />
        );
    } else if (readOnly) {
        // Browse every round of a locked card; nothing can change. Rounds
        // after a stoppage (or never scored) read "Not fought".
        const r = round;
        const s = card.rounds[r - 1];
        body = (
            <>
                <FighterRows match={match} />
                {caption && <p className="text-xs opacity-60 mt-1">{caption}</p>}
                <div className="flex justify-between items-baseline mt-3 mb-2">
                    <b className="text-xs tracking-[0.14em] uppercase text-white">{t("judge.roundN", { n: r })}</b>
                    <span className="text-xs opacity-60">
                        {card.submitted ? outcome(card) : t("judge.scored", { scored: scoredCount(card.rounds), total: ROUNDS })}
                    </span>
                </div>
                {s ? (
                    <div className="grid grid-cols-2 gap-2">
                        <ScoreFace side="red" score={s.red} other={s.blue} t={t} />
                        <ScoreFace side="blue" score={s.blue} other={s.red} t={t} />
                    </div>
                ) : (
                    <p className="py-8 text-center opacity-50 text-sm tracking-widest uppercase">
                        {t("judge.notFought")}
                    </p>
                )}
                <Totals card={card} t={t} />
            </>
        );
        cta = next && next !== current ? (
            <button type="button" className="btn btn-primary w-full" onClick={() => openMatch(next)}>
                {t("judge.resume", { n: matches.find((m) => m.boutKey === next)?.n })}
                <ArrowRight size={18} />
            </button>
        ) : (
            <AllMatchesButton onClick={() => setShowList(true)} t={t} />
        );
    } else {
        const s = card.rounds[round - 1] ?? FRESH_ROUND;
        const last = ending ? round : ROUNDS;
        const isLast = round === last;
        const missing = missingRounds(card.rounds, last, round);
        const stoppage = isStoppage(method);
        const ready = missing.length === 0 && (!stoppage || !!winner);
        const missingLabel = t("judge.missing", { rounds: missing.join(", "), count: missing.length });
        body = (
            <>
                <FighterRows match={match} />
                {caption && <p className="text-xs opacity-60 mt-1">{caption}</p>}
                <div className="flex justify-between items-baseline mt-3 mb-2">
                    <b className="text-xs tracking-[0.14em] uppercase text-white flex items-center gap-1.5">
                        {t("judge.roundN", { n: round })}
                        {roundLocked && (
                            <span className="flex items-center gap-1 opacity-70">
                                <Lock size={12} />
                                {t("judge.locked")}
                            </span>
                        )}
                    </b>
                    <span className="text-xs opacity-60">
                        {t("judge.scored", { scored: scoredCount(card.rounds), total: ROUNDS })}
                    </span>
                </div>
                {card.reopenedFrom && (
                    <p className="text-xs text-warning mb-2">
                        {t("judge.reopenedNote", { n: reopenedLast(card) })}
                    </p>
                )}
                {/* From the server's copy only: a NEXT ROUND given up on leaves
                    our copy looking like a card scored before rounds locked. */}
                {!aheadOfOrder && !ending && isLegacyScored(serverCards.current[current] ?? blankCard(current), round) && (
                    <p className="text-xs text-warning mb-2">{t("judge.legacyNote")}</p>
                )}
                {aheadOfOrder && !ending ? (
                    <p className="py-8 text-center opacity-60 text-sm">
                        {t("judge.aheadNote", { n: nextOpen })}
                    </p>
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        <ScoreFace side="red" score={s.red} other={s.blue} steppers={!roundLocked && !aheadOfOrder} onStep={step("red")} t={t} />
                        <ScoreFace side="blue" score={s.blue} other={s.red} steppers={!roundLocked && !aheadOfOrder} onStep={step("blue")} t={t} />
                    </div>
                )}
                {roundLocked && (
                    <p className="text-xs opacity-60 mt-2 text-center">{t("judge.roundLockedNote")}</p>
                )}
                <Totals card={card} t={t} />
            </>
        );
        cta = ending ? (
            <>
                <div className="flex flex-col gap-1.5 mb-2">
                    <div className={methodRow} role="radiogroup" aria-label={t("judge.end.how")}>
                        {methods.map((k) => (
                            <button
                                key={k}
                                type="button"
                                role="radio"
                                aria-checked={method === k}
                                className={twMerge("btn flex-1 min-w-0 h-10 min-h-10 px-1", method === k && "btn-primary")}
                                onClick={() => {
                                    setMethod(k);
                                    if (!isStoppage(k)) setWinner(null);
                                }}
                            >
                                {methodName(k, t)}
                            </button>
                        ))}
                    </div>
                    {stoppage && (
                        <div className="flex gap-1" role="radiogroup" aria-label={t("judge.end.winner")}>
                            {["red", "blue"].map((side) => (
                                <button
                                    key={side}
                                    type="button"
                                    role="radio"
                                    aria-checked={winner === side}
                                    className={twMerge(
                                        "btn flex-1 min-w-0 h-10 min-h-10 px-1",
                                        winner === side &&
                                            (side === "red"
                                                ? "bg-red-600 border-red-600 text-white hover:bg-red-600"
                                                : "bg-blue-600 border-blue-600 text-white hover:bg-blue-600")
                                    )}
                                    onClick={() => setWinner(side)}
                                >
                                    <Crown size={16} />
                                    {sideLabel(side)}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        className="btn btn-primary flex-1 min-w-0"
                        disabled={!ready || submitting}
                        onClick={askSubmit}
                    >
                        {submitting ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : missing.length ? (
                            missingLabel
                        ) : stoppage ? (
                            winner ? (
                                <>
                                    <Lock size={16} />
                                    {t("judge.end.submitStoppage", { method: methodName(method, t), side: sideLabel(winner) })}
                                </>
                            ) : (
                                t("judge.end.pickWinner")
                            )
                        ) : (
                            <>
                                <Lock size={16} />
                                {t("judge.end.submitDecision", { n: round })}
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        className="btn btn-square btn-outline flex-none"
                        aria-label={t("judge.end.cancel")}
                        onClick={() => setEnding(false)}
                        disabled={submitting}
                    >
                        <X size={20} />
                    </button>
                </div>
            </>
        ) : (
            <>
            {aheadOfOrder ? (
                // Looking ahead: rounds lock in order, so the way on is back.
                <button type="button" className="btn btn-primary w-full" onClick={() => pickRound(nextOpen)}>
                    {t("judge.goToRound", { n: nextOpen })}
                    <ArrowRight size={18} />
                </button>
            ) : readyToResubmit(card) ? (
                <button
                    type="button"
                    className="btn btn-primary w-full"
                    disabled={submitting}
                    onClick={() => startResubmit(card)}
                >
                    <Lock size={16} />
                    {t("judge.resubmitCard")}
                </button>
            ) : (
            <>
            {/* Ending the match is one tap away: each opens the end panel with
                its method chosen (DECISION = end after this round on points),
                and the winner, SUBMIT and PIN still follow. */}
            <div className={twMerge(methodRow, "mb-2")}>
                {methods.map((k) => (
                    <button
                        key={k}
                        type="button"
                        className="btn btn-outline flex-1 min-w-0 h-10 min-h-10"
                        onClick={() => {
                            setEnding(true);
                            setMethod(k);
                            setWinner(null);
                        }}
                    >
                        {methodName(k, t)}
                    </button>
                ))}
            </div>
            <div className="flex gap-2">
                {isLast ? (
                    <button
                        type="button"
                        className="btn btn-primary flex-1 min-w-0"
                        disabled={missing.length > 0 || submitting}
                        onClick={askSubmit}
                    >
                        {submitting ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : missing.length ? (
                            missingLabel
                        ) : (
                            <>
                                <Lock size={16} />
                                {t("judge.submitCard")}
                            </>
                        )}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-primary flex-1 min-w-0"
                        onClick={advance}
                        disabled={committing}
                    >
                        {t("judge.nextRound")}
                        <ArrowRight size={18} />
                    </button>
                )}
            </div>
            </>
            )}
            </>
        );
    }

    const tabs =
        card.submitted && fresh ? null : (
            <RoundTabs
                card={card}
                round={round}
                cutFrom={readOnly ? card.endAt : ending ? round : null}
                onPick={pickRound}
            />
        );

    // Yellow, under the header, until OK: a toast at the top of the screen
    // is easy to miss while watching the fight.
    const orderBanner = orderNote && orderNote.boutKey === current && (
        <div
            role="alert"
            className="flex items-center gap-3 bg-warning text-black px-3 py-2.5 motion-safe:animate-[pulse_1s_ease-in-out_3]"
        >
            <ListOrdered size={22} className="flex-none" aria-hidden="true" />
            <p className="flex-1 min-w-0 leading-snug">
                <b className="block text-xs font-bold uppercase tracking-wider">{t("judge.orderBanner.title")}</b>
                <span className="font-semibold">
                    {orderNote.kind === "follow"
                        ? t("judge.orderBanner.follow", {
                              n: match.n,
                              red: shortName(match.red.name),
                              blue: shortName(match.blue.name),
                          })
                        : t("judge.orderBanner.renumbered", { n: match.n })}
                </span>
            </p>
            <button
                type="button"
                className="btn btn-sm flex-none bg-black text-white border-black hover:bg-black/80 hover:border-black"
                onClick={() => setOrderNote(null)}
            >
                {t("judge.orderBanner.ok")}
            </button>
        </div>
    );

    const scorer = (
        <>
            <div className="flex-none px-3 pt-3 flex flex-col gap-2">
                {header}
                {orderBanner}
                {tabs}
            </div>
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 pt-1 pb-3">
                {body}
            </div>
            <Cta>{cta}</Cta>
        </>
    );

    const confirmTot = tallyRounds(committed());
    const confirmSummary = ending && isStoppage(method)
        ? `${methodName(method, t)} · ${winner ? sideLabel(winner) : ""} · ${t("judge.roundN", { n: round }).toUpperCase()}`
        : `${confirmTot.red} — ${confirmTot.blue}${ending ? ` · ${t("judge.outcome.endedAfter", { n: round })}` : ""}`;
    const confirmSheet = confirmOpen && (
        <div className="modal modal-open modal-bottom sm:modal-middle" role="dialog" aria-modal="true" aria-labelledby="judge-confirm-title">
            <form
                className="modal-box border flex flex-col gap-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
                onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                }}
            >
                <div>
                    <h3 id="judge-confirm-title" className="font-bold text-lg">
                        {t("judge.confirm.title", { n: match.n })}
                    </h3>
                    <p className="text-2xl font-bold text-white tabular-nums mt-2">{confirmSummary}</p>
                    <p className="text-xs opacity-60 mt-1">{t("judge.confirm.note")}</p>
                </div>
                <label htmlFor="judge-confirm-pin" className="font-semibold">
                    {t("judge.pin.label")}
                </label>
                <input
                    id="judge-confirm-pin"
                    ref={confirmPinRef}
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    autoComplete="off"
                    className="input input-bordered text-center text-lg tracking-widest"
                    placeholder="••••"
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    disabled={submitting}
                />
                <div className="flex gap-2">
                    <button
                        type="button"
                        className="btn flex-1"
                        onClick={() => setConfirmOpen(false)}
                        disabled={submitting}
                    >
                        {t("judge.confirm.cancel")}
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary flex-1"
                        disabled={confirmPin.length !== 4 || submitting}
                    >
                        {submitting ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <>
                                <Lock size={16} />
                                {t("judge.confirm.submit")}
                            </>
                        )}
                    </button>
                </div>
            </form>
            <button
                type="button"
                className="modal-backdrop"
                aria-hidden="true"
                tabIndex={-1}
                onClick={() => !submitting && setConfirmOpen(false)}
            />
        </div>
    );

    return (
        <main className="flex-1 min-h-0 flex">
            {confirmSheet}
            {/* Tablet and up: the running order is a permanent left column. */}
            <aside className="hidden md:flex flex-col w-64 flex-none border-r-2 border-base-content/10 min-h-0">
                <div className="flex-none px-3 pt-3 pb-2 flex items-center justify-between gap-2">
                    <b className="text-xs tracking-widest uppercase opacity-60 truncate">
                        {event.isTest && <span className="text-warning">{t("judge.testBadge")} · </span>}
                        {t("judge.list.title", { name: event.name, count: matches.length })}
                    </b>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
                    <MatchList matches={matches} cards={cards} current={current} onOpen={openMatch} t={t} />
                </div>
            </aside>
            <section className="flex-1 min-w-0 flex flex-col">
                {showList ? <div className="md:hidden flex-1 min-h-0 flex flex-col">{listScreen}</div> : null}
                <div className={twMerge("flex-1 min-h-0 flex flex-col", showList && "hidden md:flex")}>
                    {scorer}
                </div>
            </section>
        </main>
    );
}

function Cta({ children }) {
    return (
        <div className="flex-none px-3 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t-2 border-base-content/10 bg-base-100">
            {children}
        </div>
    );
}

// Shown when no card is left to score. On tablets the list is already on
// screen, so there is nothing to open.
function AllMatchesButton({ onClick, t }) {
    return (
        <>
            <button type="button" className="btn btn-primary w-full md:hidden" onClick={onClick}>
                {t("judge.allMatches")}
            </button>
            <p className="hidden md:block text-center text-xs tracking-widest uppercase opacity-60 py-3">
                {t("judge.allSubmitted")}
            </p>
        </>
    );
}

/* ──────────────────────────────── page ──────────────────────────────── */

export default function Judge() {
    const { token } = useParams();
    const { t } = useTranslation();
    const [session, setSession] = useState(() => store.get(SESSION_KEY(token)));
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState(false);

    const endSession = useCallback(
        (notify = true) => {
            store.remove(SESSION_KEY(token));
            setSession(null);
            setData(null);
            if (notify) toast.error(t("judge.errors.sessionEnded"), TOAST);
        },
        [t, token]
    );

    const load = useCallback(async () => {
        if (!session) return;
        setLoadError(false);
        try {
            const res = await judgeGetEvent(session);
            if (res?.success) {
                setData(res);
            } else if (res?.code === "SESSION_ENDED") {
                endSession();
            } else {
                setLoadError(true);
            }
        } catch (error) {
            console.error("judgeGetEvent failed:", error);
            setLoadError(true);
        }
    }, [endSession, session]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSignedIn = (next) => {
        store.set(SESSION_KEY(token), next);
        setSession(next);
    };

    const sessionEnded = useMemo(() => () => endSession(true), [endSession]);

    let content;
    if (!session) {
        content = <PinScreen token={token} onSignedIn={handleSignedIn} />;
    } else if (loadError) {
        content = (
            <main className="flex-1 flex-center p-4">
                <div className="flex flex-col items-center gap-4 text-center">
                    <p>{t("judge.errors.loadFailed")}</p>
                    <button type="button" className="btn btn-outline btn-sm" onClick={load}>
                        {t("judge.retry")}
                    </button>
                </div>
            </main>
        );
    } else if (!data) {
        content = (
            <main className="flex-1 flex-center">
                <div className="loading loading-lg loading-bars" />
            </main>
        );
    } else {
        content = (
            <JudgeBoard
                key={`${session.sessionId}`}
                data={data}
                session={session}
                token={token}
                onSessionEnded={sessionEnded}
            />
        );
    }

    return <JudgeShell barOnPhone={!data || !session}>{content}</JudgeShell>;
}
